import AssistChip from 'LuxKit/Chips/AssistChip'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import React, { ChangeEventHandler, useCallback, useContext, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import BusinessTravellerSelectHotelsTravellersContent from './BusinessTravellerSelectHotelsTravellersContent'
import TextInput from 'components/Common/Form/Input/TextInput'
import IconButton from 'LuxKit/Button/IconButton'
import Modal from 'LuxKit/Modal/Modal'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  onApply: () => void
  setOccupants: (employeeIds: Array<string>, guestCount: number, roomCount?: number) => void
}

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  guestCount: number
}

function BusinessTravellerSelectHotelsTravellersModal(props: Props & MappedStateProps) {
  const { travellerEmployees, guestCount, setOccupants, onApply } = props

  const [fieldActive, setFieldActive] = useState(false)

  const [searchTerm, setSearchTerm] = useState<string>('')
  const handleSearchTermChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchTerm(event.currentTarget.value)
  }, [])

  const globalSearchState = useContext(GlobalSearchStateContext)
  const occupancies = useMemo(() => globalSearchState.occupancies ?? [], [globalSearchState.occupancies])

  const totalTravellers = travellerEmployees.length + guestCount

  const onAddEmployee = useCallback((employeeId: string) => {
    const selectedEmployeeIds = [...travellerEmployees.map(employee => employee.id), employeeId]
    const newOccupantCount = selectedEmployeeIds.length + guestCount

    // Add new room when adding employee
    if (newOccupantCount > 1) {
      setOccupants(selectedEmployeeIds, guestCount, occupancies.length + 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount)
    }
  }, [guestCount, travellerEmployees, setOccupants, occupancies])

  const onRemoveEmployee = useCallback((employeeId: string) => {
    const selectedEmployeeIds = travellerEmployees.filter(employee => employee.id !== employeeId).map(employee => employee.id)
    const newOccupantCount = selectedEmployeeIds.length + guestCount
    // Remove a room if there will be any empty rooms
    if (newOccupantCount < occupancies.length) {
      setOccupants(selectedEmployeeIds, guestCount, (occupancies.length - 1) || 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount)
    }
  }, [travellerEmployees, guestCount, occupancies.length, setOccupants])

  const onRemoveGuest = useCallback(() => {
    const selectedEmployeeIds = travellerEmployees.map(employee => employee.id)
    const newOccupantCount = selectedEmployeeIds.length + guestCount - 1
    // Remove a room if there will be any empty rooms
    if (newOccupantCount < occupancies.length) {
      setOccupants(selectedEmployeeIds, guestCount - 1, (occupancies.length - 1) || 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount - 1)
    }
  }, [travellerEmployees, guestCount, occupancies.length, setOccupants])

  const onAddGuest = useCallback(() => {
    const newOccupantCount = travellerEmployees.length + guestCount + 1

    // Add new room when adding guest
    if (newOccupantCount > 1) {
      setOccupants(travellerEmployees.map(employee => employee.id), guestCount + 1, occupancies.length + 1)
    } else {
      setOccupants(travellerEmployees.map(employee => employee.id), guestCount + 1)
    }
  }, [travellerEmployees, guestCount, setOccupants, occupancies])

  const modalElementContext = useModalElementContext()

  const dismiss = useCallback(() => {
    modalElementContext.resolve()
    onApply()
  }, [modalElementContext, onApply])

  return (
    <Modal
      mode="drawer"
      title="Who's travelling"
      subtitle={`${totalTravellers} ${totalTravellers === 1 ? 'traveller' : 'travellers'} added`}
      primaryActionText="Continue"
      onPrimaryActionClick={dismiss}
      headerExtension={<Group direction="vertical" gap={12}>
        <TextInput
          type="text"
          placeholder="Add traveller"
          onChange={handleSearchTermChange}
          onFocus={() => setFieldActive(true)}
          endIcon={<>
            {!fieldActive && <LineSearchIcon />}
            {fieldActive && <IconButton
              kind="tertiary"
              horizontalOutdent="end"
              onClick={(e) => { e.preventDefault(); setFieldActive(false) }}
            >
              <LineTimesIcon />
            </IconButton>}
          </>}
          noValidationSpacing
        />
        <Group direction="horizontal" gap={8} wrap="wrap">
          {travellerEmployees.map(employee => (
            <AssistChip
              onClick={() => onRemoveEmployee(employee.id)}
              key={employee.id}
              endIcon={<LineTimesIcon />}
              variant="outline"
              size="small"
            >
              {employee.firstName} {employee.lastName}
            </AssistChip>
          ))}
          {Array.from({ length: guestCount }).map((_, index) => (
            <AssistChip
              onClick={() => onRemoveGuest()}
              key={index}
              endIcon={<LineTimesIcon />}
              variant="outline"
              size="small"
            >
              Guest traveller
            </AssistChip>
          ))}
        </Group>
        <Group direction="horizontal" gap={12}>
          <TextButton
            size="medium"
            kind="tertiary"
            startIcon={<LinePlusIcon />}
            onClick={onAddGuest}
            horizontalOutdent="start"
          >
            Add guest traveller
          </TextButton>
        </Group>
      </Group>}
    >
      <BusinessTravellerSelectHotelsTravellersContent
        inputActive={fieldActive}
        searchTerm={searchTerm}
        onAddEmployee={onAddEmployee}
        onRemoveEmployee={onRemoveEmployee}
      />
    </Modal>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    guestCount: state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectHotelsTravellersModal)
